/**
 * lostPlayProbe — two inbound-RTP reads a short gap apart, and what moved
 * between them.
 *
 * Telemetry only. It answers which part of the chain is silent for a peer the
 * playhead check doubts: no RTP arriving, RTP arriving but nothing draining
 * the jitter buffer, or both moving and the silence further down. Nothing
 * decides a verdict or a repair on it.
 *
 * | rtpArriving | samplesPulled | Reads as                              |
 * |-------------|---------------|---------------------------------------|
 * | false       | any           | no RTP arriving                       |
 * | true        | false         | jitter buffer not being drained       |
 * | true        | true          | decode running; silence after it      |
 *
 * `totalSamplesReceived` rises each time 10ms is pulled out of the jitter
 * buffer. What pulls differs by engine, so `samplesPulled` is not a playout
 * check:
 * - Chromium: the output sink's render callback pulls, so `false` means the
 *   output is not pulling.
 * - WebKit: a timer in `LibWebRTCAudioModule` pulls between `StartPlayout`
 *   and `StopPlayout`, whatever the element or output device is doing. `false`
 *   means that timer stopped. A dead output shows as `true`/`true`, so on
 *   WebKit `true`/`true` with a frozen playhead is the output-stall case.
 *
 * A flag is `unknown`, never `false`, when a read failed, the field is missing
 * or a counter went backwards (a receiver swapped under the reads). A `false`
 * built from a missing value would count as evidence of the fault it cannot
 * see.
 */

import type { AudioReceiverStats } from './audioReceiverStats'

/** A string rather than a boolean so GA can hold all three values. */
export type ProbeFlag = 'true' | 'false' | 'unknown'

export interface ProbeDeltas {
  /** `packetsReceived` rose between the reads. */
  rtpArriving: ProbeFlag
  /** `totalSamplesReceived` rose between the reads. Not a playout check; see the header. */
  samplesPulled: ProbeFlag
  /** -1 when unknown, so a failed read stays visible in a GROUP BY. */
  packetsDelta: number
  samplesDelta: number
  concealedDelta: number
}

export interface LostPlayProbeResult extends ProbeDeltas {
  /** When the first read started. */
  startedAt: number
  /** When the second read finished. */
  endedAt: number
  /**
   * Time between the two reads as measured, not as asked for. A hidden tab
   * throttles timers, so the gap can stretch well past the setting.
   */
  probeGapMs: number
}

/** `after - before`, or undefined when either side is missing or it went backwards. */
function counterDelta(
  before: number | undefined,
  after: number | undefined
): number | undefined {
  if (typeof before !== 'number' || typeof after !== 'number') return undefined
  const delta = after - before
  return delta < 0 ? undefined : delta
}

function flagFor(delta: number | undefined): ProbeFlag {
  if (delta === undefined) return 'unknown'
  return delta > 0 ? 'true' : 'false'
}

/** The flags and deltas between two reads of the same receiver. */
export function probeDeltas(
  before: AudioReceiverStats | undefined,
  after: AudioReceiverStats | undefined
): ProbeDeltas {
  const packets = counterDelta(before?.packetsReceived, after?.packetsReceived)
  const samples = counterDelta(
    before?.totalSamplesReceived,
    after?.totalSamplesReceived
  )
  const concealed = counterDelta(
    before?.concealedSamples,
    after?.concealedSamples
  )
  return {
    rtpArriving: flagFor(packets),
    samplesPulled: flagFor(samples),
    packetsDelta: packets ?? -1,
    samplesDelta: samples ?? -1,
    concealedDelta: concealed ?? -1,
  }
}

/**
 * Read, wait `gapMs`, read again. `read` must not throw: a failed read is
 * undefined, and comes back as `unknown` flags.
 */
export async function runLostPlayProbe(deps: {
  read: () => Promise<AudioReceiverStats | undefined>
  now: () => number
  sleep: (ms: number) => Promise<void>
  gapMs: number
}): Promise<LostPlayProbeResult> {
  const startedAt = deps.now()
  const before = await deps.read()
  const firstReadAt = deps.now()
  await deps.sleep(deps.gapMs)
  const after = await deps.read()
  const endedAt = deps.now()
  return {
    ...probeDeltas(before, after),
    startedAt,
    endedAt,
    probeGapMs: endedAt - firstReadAt,
  }
}

/**
 * The next value of a peer's consecutive-flat-sweep count.
 *
 * Up by one when either flag is `false` or the playhead check called the peer
 * frozen on the same sweep. Back to 0 only on positive evidence: both flags
 * `true` and the playhead moving. Anything else (an `unknown` flag with nothing
 * else flat) leaves it where it was, because a failed read says nothing either
 * way.
 */
export function nextFlatStreak(
  previous: number,
  deltas: Pick<ProbeDeltas, 'rtpArriving' | 'samplesPulled'>,
  currentTimeFrozen: boolean
): number {
  if (
    deltas.rtpArriving === 'false' ||
    deltas.samplesPulled === 'false' ||
    currentTimeFrozen
  ) {
    return previous + 1
  }
  if (deltas.rtpArriving === 'true' && deltas.samplesPulled === 'true') {
    return 0
  }
  return previous
}
